import React, { useState, useEffect, useCallback, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Alert, Button, Table, Badge } from "react-bootstrap";
import axios from "axios";
import LabelFieldComponent from "../../../../components/fields/LabelFieldComponent";
import PageLayout from "../../../../layouts/PageLayout";

export default function MarketPlacerIndex() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [marketPlacers, setMarketPlacers] = useState([]);
    const [search, setSearch] = useState("");

    const fetchMarketPlacers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/market-placer`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );
            if (response.data?.success) {
                setMarketPlacers(response.data.data || []);
            } else {
                setError(response.data?.message || "Failed to fetch market placers.");
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to fetch list.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMarketPlacers();
    }, [fetchMarketPlacers]);

    const filteredData = marketPlacers.filter((item) => {
        const q = search.toLowerCase();
        return (
            item.name?.toLowerCase().includes(q) ||
            item.email?.toLowerCase().includes(q) ||
            item.nickname?.toLowerCase().includes(q)
        );
    });

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {t("Market Placers")} ({filteredData.length})
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">{t("Home")}</Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">{t("Market Placers")}</li>
                                </ul>
                            </div>
                            <Button
                                variant="primary"
                                onClick={() => navigate("/market-placer/create")}
                            >
                                <i className="material-icons me-1 vertical-middle">add</i>
                                {t("Add Market Placer")}
                            </Button>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card p-4 rounded-4 shadow-sm border-0 bg-white">
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError("")} className="mb-4">
                                {error}
                            </Alert>
                        )}

                        <Row className="g-3 align-items-end mb-4">
                            <Col md={6} xl={4}>
                                <LabelFieldComponent
                                    type="search"
                                    placeholder="Search by Name, Email or Nickname..."
                                    label="Search"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </Col>
                        </Row>

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">{t("Loading Market Placers...")}</p>
                            </div>
                        ) : (
                            <div className="mc-table-responsive" style={{ overflowX: "auto" }}>
                                <Table hover className="mc-table align-middle" style={{ tableLayout: "fixed", width: "100%", minWidth: "900px" }}>
                                    <thead className="mc-table-head primary">
                                        <tr>
                                            <th style={{ width: "16%" }}>Name</th>
                                            <th style={{ width: "14%" }}>Nickname</th>
                                            <th style={{ width: "22%" }}>Email</th>
                                            <th style={{ width: "14%" }}>Phone</th>
                                            <th style={{ width: "12%" }}>eBay Account</th>
                                            <th style={{ width: "8%" }}>Status</th>
                                            <th style={{ width: "6%" }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="mc-table-body">
                                        {filteredData.length > 0 ? (
                                            filteredData.map((item) => (
                                                <tr key={item.id}>
                                                    <td className="fw-semibold text-truncate">{item.name || "N/A"}</td>
                                                    <td className="text-truncate">{item.nickname || "N/A"}</td>
                                                    <td className="text-break" style={{ wordBreak: "break-word" }}>
                                                        {item.email || "N/A"}
                                                    </td>
                                                    <td className="text-nowrap">{item.phone || "N/A"}</td>
                                                    <td>
                                                        <Badge bg="light" className="text-dark border text-truncate max-w-100">
                                                            {item.mplacer?.ebay_username || "N/A"}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${item.status ? "bg-success" : "bg-secondary"}`}>
                                                            {item.status ? "Active" : "Inactive"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="mc-table-action d-flex align-items-center gap-2">
                                                            <button
                                                                type="button"
                                                                className="material-icons border-0 bg-transparent text-primary"
                                                                onClick={() =>
                                                                    navigate(`/market-placer/edit/${item.id}`, {
                                                                        state: { marketPlacer: item },
                                                                    })
                                                                }
                                                                title="Edit"
                                                            >
                                                                edit
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={8} className="text-center py-4 text-muted">
                                                    {t("No market placers found")}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}